import React, { useRef, useState } from "react";

import CollapseIcon from "@uui/Minimize01";
import EditIcon from "@uui/Pencil01";
import { filter, find, isEmpty, iteratee, negate } from "lodash/fp";

import { Collapse, IconButton, Theme, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";

import type {
  GeneralAccess,
  GeneralAccessLevel,
  GeneralAccessPattern,
  SessionACLResponse,
} from "@app/common/types";
import type { EnterpriseWorkspace } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { lightOrDark } from "@app/embedded/theme/utils";

import Dropdown from "@rui/Dropdown";
import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import Stack, { Row } from "@rui/Stack";

import LinkAccessUpdateConfirm from "./LinkAccessUpdateConfirm";

interface LinkAccessProps {
  accessList: SessionACLResponse["entityAccess"];
  onChange: (
    generalAccessPattern: GeneralAccessPattern,
    accessLevel: GeneralAccessLevel,
    entitiesToUpdate?: SessionACLResponse["entityAccess"]
  ) => void;
  generalAccess: GeneralAccess;
  fullScreen?: boolean;
  fullWidth?: boolean;
  hideLabel?: boolean;
  disabled?: boolean;
}

const WHO_CAN_ACCESS: { value: GeneralAccessPattern; label: string }[] = [
  { value: "people_with_access", label: "People with access" },
  { value: "workspace_members", label: "Workspace members" },
  { value: "anyone", label: "Anyone with the link" },
];

const WHO_CAN_ACCESS_NO_WORKSPACE = filter(
  negate(iteratee({ value: "workspace_members" })),
  WHO_CAN_ACCESS
);

const LINK_ACCESS_LEVEL: {
  value: GeneralAccessLevel;
  label: string;
  description: string;
  deprecated?: boolean;
}[] = [
  {
    value: "viewer_recap_only",
    label: "Recap only",
    description: "Meeting notes and transcript",
    deprecated: true,
  },
  {
    value: "viewer_full",
    label: "Full report",
    description: "Notes, transcript, Deep Dive metrics",
  },
];

const AccessDescription = ({
  workspace,
  whoCanAccess,
  linkAccessLevel,
}: {
  workspace: EnterpriseWorkspace;
  whoCanAccess: GeneralAccessPattern;
  linkAccessLevel: GeneralAccessLevel;
}) => {
  if (whoCanAccess === "people_with_access") {
    return (
      <Typography
        variant="bodySecondaryReg"
        sx={{ color: "shades.neutral.500" }}
      >
        Only{" "}
        <Typography variant="bodySecondaryMed">
          people the report is shared with
        </Typography>{" "}
        can access with a link
      </Typography>
    );
  }

  const viewAccess =
    linkAccessLevel === "viewer_full"
      ? "full meeting report"
      : "recap meeting report";

  if (whoCanAccess === "workspace_members" && !isEmpty(workspace.id)) {
    return (
      <Typography
        variant="bodySecondaryReg"
        sx={{
          color: (theme: Theme) =>
            lightOrDark("shades.neutral.500", "shades.neutral.50")(theme),
        }}
      >
        Only{" "}
        <Typography variant="bodySecondaryMed">
          {workspace.name} members
        </Typography>{" "}
        can view the{" "}
        <Typography variant="bodySecondaryMed">{viewAccess}</Typography>
      </Typography>
    );
  }

  return (
    <Typography
      variant="bodySecondaryReg"
      sx={{
        color: (theme: Theme) =>
          lightOrDark("shades.neutral.500", "shades.neutral.50")(theme),
      }}
    >
      <Typography variant="bodySecondaryMed">Anyone with the link</Typography>{" "}
      can view the{" "}
      <Typography variant="bodySecondaryMed">{viewAccess}</Typography>
    </Typography>
  );
};

const getEnabledAccessOptions = (
  workspace: EnterpriseWorkspace,
  generalAccess: GeneralAccess
) => {
  const accessOptions = isEmpty(workspace.id)
    ? WHO_CAN_ACCESS_NO_WORKSPACE
    : WHO_CAN_ACCESS;

  const patternOptions = generalAccess.accessPatternOptions;

  if (!patternOptions) {
    return accessOptions;
  }

  return filter(
    (accessOption) => patternOptions.includes(accessOption.value),
    accessOptions
  );
};

// Return non-deprecated options if the selected option is not deprecated
// otherwise return all items if the selected option is deprecated
// This way we can be compatible with currently selected deprecated options
// while also preventing users from selecting a changing to a deprecated option
const getAccessLevelOptions = (selectedOption?: GeneralAccessLevel) => {
  const activeOptions = filter((item) => !item.deprecated, LINK_ACCESS_LEVEL);

  if (!selectedOption) return activeOptions;
  const selectedLevel = find({ value: selectedOption }, LINK_ACCESS_LEVEL);
  if (selectedLevel?.deprecated) {
    return LINK_ACCESS_LEVEL;
  }
  return activeOptions;
};

const getEntitiesToUpdate = (
  accessPattern: GeneralAccessPattern,
  accessLevel: GeneralAccessLevel,
  accessList: LinkAccessProps["accessList"]
) => {
  // currently the only situation in which we will auto-update individual entities on
  // a link access change is if full report access is being granted to anyone with the link.
  // (individual entities will be updated to also have full report access). if that's not
  // the access pattern/level that is being granted, no entities should be affected.
  if (accessPattern !== "anyone" || accessLevel === "viewer_recap_only") {
    return [];
  }

  return filter({ accessLevel: "viewer_recap_only" }, accessList);
};

interface PendingAccessChange {
  accessLevel: GeneralAccessLevel;
  accessPattern: GeneralAccessPattern;
  affectedEntities: SessionACLResponse["entityAccess"];
}

const LinkAccess = ({
  accessList,
  onChange,
  generalAccess,
  fullScreen,
  fullWidth,
  hideLabel,
  disabled,
}: LinkAccessProps) => {
  const [expanded, setExpanded] = useState(!fullScreen);
  const [pendingAccessChange, setPendingAccessChange] =
    useState<PendingAccessChange | null>(null);

  const accessLevel = generalAccess.accessLevel ?? "viewer_full";

  const handleChangeAccess = (event: SelectChangeEvent) => {
    const newGeneralAccess = event.target.value as GeneralAccessPattern;

    const entitiesToConfirm = getEntitiesToUpdate(
      newGeneralAccess,
      accessLevel,
      accessList
    );

    if (isEmpty(entitiesToConfirm)) {
      onChange(newGeneralAccess, accessLevel);
    } else {
      setPendingAccessChange({
        accessLevel,
        accessPattern: newGeneralAccess,
        affectedEntities: entitiesToConfirm,
      });
    }
  };

  const handleChangeAccessLevel = (event: SelectChangeEvent) => {
    const newAccessLevel = event.target.value as GeneralAccessLevel;

    const entitiesToConfirm = getEntitiesToUpdate(
      generalAccess.accessPattern,
      newAccessLevel,
      accessList
    );

    if (isEmpty(entitiesToConfirm)) {
      onChange(generalAccess.accessPattern, newAccessLevel);
    } else {
      setPendingAccessChange({
        accessLevel: newAccessLevel,
        accessPattern: generalAccess.accessPattern,
        affectedEntities: entitiesToConfirm,
      });
    }
  };

  const handleAccessUpdateCancel = () => {
    setPendingAccessChange(null);
  };

  const handleAccessUpdateConfirm = () => {
    if (!pendingAccessChange) return;

    onChange(
      pendingAccessChange.accessPattern,
      pendingAccessChange.accessLevel,
      pendingAccessChange.affectedEntities
    );
    setPendingAccessChange(null);
  };

  const { workspace } = useWorkspace();
  const enabledAccessOptions = getEnabledAccessOptions(
    workspace,
    generalAccess
  );

  const selectedAccessLevel = getSelectedFromOptions(
    LINK_ACCESS_LEVEL,
    accessLevel
  );

  const enabledAccessLevelOptions = getAccessLevelOptions(
    selectedAccessLevel?.value
  );

  // we want to keep showing the dropdown
  // if the dropdown was initialized with a non-empty list of options
  const persistShowingAccessLevelDropdown = useRef(
    !isEmpty(filter({ deprecated: true }, enabledAccessLevelOptions))
  );

  const showAccessLevelDropdown =
    !fullScreen &&
    generalAccess.accessPattern !== "people_with_access" &&
    persistShowingAccessLevelDropdown.current;

  const selectedLinkAccessPattern = getSelectedFromOptions(
    enabledAccessOptions,
    generalAccess.accessPattern
  );

  return (
    <>
      <Stack spacing={1}>
        {!hideLabel && (
          <Row spacing={1} variant="apart">
            <Row spacing={1} variant="left">
              <Typography
                variant="bodyPrimaryMed"
                sx={{
                  color: (theme: Theme) =>
                    lightOrDark(
                      "shades.neutral.800",
                      "shades.neutral.50"
                    )(theme),
                }}
              >
                Link access
                {fullScreen && (
                  <Typography variant="bodyPrimaryReg">
                    {expanded || !selectedLinkAccessPattern?.label
                      ? ""
                      : `: ${selectedLinkAccessPattern?.label}`}
                  </Typography>
                )}
              </Typography>
            </Row>
            {!fullScreen ? null : expanded ? (
              <IconButton size="small" onClick={() => setExpanded(false)}>
                <CollapseIcon
                  sx={{
                    fontSize: "16px",
                    color: (theme: Theme) =>
                      lightOrDark(
                        "shades.neutral.500",
                        "shades.neutral.50"
                      )(theme),
                  }}
                />
              </IconButton>
            ) : (
              <IconButton size="small" onClick={() => setExpanded(true)}>
                <EditIcon
                  sx={{
                    fontSize: "16px",
                    color: (theme: Theme) =>
                      lightOrDark(
                        "shades.neutral.500",
                        "shades.neutral.50"
                      )(theme),
                  }}
                />
              </IconButton>
            )}
          </Row>
        )}
        <Collapse in={expanded || !fullScreen} unmountOnExit>
          <Stack spacing={1}>
            <Row spacing={1} sx={{ maxWidth: "552px" }}>
              <Dropdown
                name="link-access"
                onChange={handleChangeAccess}
                selectedItem={selectedLinkAccessPattern}
                placeholder="Who can access"
                wrapperSx={{
                  width: "100%",
                  // account for spacing between the two when only one is shown
                  maxWidth:
                    fullScreen || fullWidth ? undefined : "calc(50% - 5px)",
                }}
                disabled={disabled}
              >
                {enabledAccessOptions.map((item) => {
                  return (
                    <DropdownMenuItem
                      key={item.value}
                      {...item}
                      selected={item.value === generalAccess.accessPattern}
                    />
                  );
                })}
              </Dropdown>
              {showAccessLevelDropdown && (
                <Dropdown
                  name="link-access-level"
                  onChange={handleChangeAccessLevel}
                  selectedItem={selectedAccessLevel}
                  wrapperSx={{ width: "100%" }}
                >
                  {(persistShowingAccessLevelDropdown
                    ? LINK_ACCESS_LEVEL
                    : enabledAccessLevelOptions
                  ).map((item) => {
                    return (
                      <DropdownMenuItem
                        key={item.value}
                        label={item.label}
                        value={item.value}
                        selected={item.value === accessLevel}
                      />
                    );
                  })}
                </Dropdown>
              )}
            </Row>
            <AccessDescription
              workspace={workspace}
              linkAccessLevel={accessLevel}
              whoCanAccess={generalAccess.accessPattern}
            />
          </Stack>
        </Collapse>
      </Stack>
      <LinkAccessUpdateConfirm
        pendingUpdate={pendingAccessChange}
        onClose={handleAccessUpdateCancel}
        onConfirm={handleAccessUpdateConfirm}
      />
    </>
  );
};

export default LinkAccess;
